import React, { useState } from 'react';
import { sans, green, greenLight, useTheme } from '../../tokens';

const sectionTitleStyle = (t) => ({
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: t.textMuted,
  fontFamily: sans,
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid ' + t.border,
});

const toggleStyle = (enabled, t) => ({
  width: 36,
  height: 20,
  borderRadius: 10,
  background: enabled ? green : t.border,
  position: 'relative',
  cursor: 'pointer',
  transition: 'background 0.15s',
  flexShrink: 0,
});

const toggleKnob = (enabled) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#fff',
  position: 'absolute',
  top: 2,
  left: enabled ? 18 : 2,
  transition: 'left 0.15s',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
});

const CHANNELS = ['inApp', 'email', 'slack'];
const CHANNEL_LABELS = { inApp: 'In-App', email: 'Email', slack: 'Slack' };

const EVENT_GROUPS = [
  {
    group: 'Close',
    events: [
      { key: 'closeDeadline', label: 'Checklist deadline approaching' },
      { key: 'periodLocked', label: 'Period locked' },
    ],
  },
  {
    group: 'Equity',
    events: [
      { key: 'grantFinalized', label: 'Grant finalized' },
      { key: 'poolThreshold', label: 'Pool below threshold' },
      { key: 'spccThreshold', label: 'SPCC threshold alert' },
      { key: 'boardApproval', label: 'Board approval pending' },
    ],
  },
  {
    group: 'Financial',
    events: [
      { key: 'runwayThreshold', label: 'Runway below threshold' },
      { key: 'burnExceeds', label: 'Burn exceeds budget' },
      { key: 'bankBalanceLow', label: 'Bank balance low' },
    ],
  },
  {
    group: 'Investor Portal',
    events: [
      { key: 'docUploaded', label: 'Document uploaded' },
      { key: 'reportPublished', label: 'Report published' },
      { key: 'capTableUpdated', label: 'Cap table updated' },
    ],
  },
  {
    group: 'System',
    events: [
      { key: 'syncFailure', label: 'Sync failure' },
      { key: 'newDeviceLogin', label: 'New device login' },
      { key: 'exportCompleted', label: 'Export completed' },
    ],
  },
];

function buildInitialToggles() {
  const toggles = {};
  EVENT_GROUPS.forEach(g => {
    g.events.forEach(e => {
      toggles[e.key] = { inApp: true, email: true, slack: false };
    });
  });
  return toggles;
}

export default function NotificationsSettings() {
  const t = useTheme();
  const savedNotif = JSON.parse(localStorage.getItem('finkloz_notification_settings') || 'null');
  const [channelEnabled, setChannelEnabled] = useState(savedNotif?.channelEnabled || { inApp: true, email: true, slack: false });
  const [eventToggles, setEventToggles] = useState(() => savedNotif?.eventToggles || buildInitialToggles());
  const [frequency, setFrequency] = useState(savedNotif?.frequency || 'realtime');
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const markDirty = () => setDirty(true);

  const handleSave = () => {
    localStorage.setItem('finkloz_notification_settings', JSON.stringify({
      channelEnabled, eventToggles, frequency,
    }));
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const toggleChannel = (ch) => {
    setChannelEnabled(prev => ({ ...prev, [ch]: !prev[ch] }));
    markDirty();
  };

  const toggleEvent = (eventKey, channel) => {
    setEventToggles(prev => ({
      ...prev,
      [eventKey]: { ...prev[eventKey], [channel]: !prev[eventKey][channel] },
    }));
    markDirty();
  };

  return (
    <div style={{ maxWidth: 800 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Notifications
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Configure notification channels and event preferences.
      </p>

      {/* Channel Toggles */}
      <div style={sectionTitleStyle(t)}>Channels</div>

      <div style={{ display: 'flex', gap: 24, marginBottom: 32 }}>
        {CHANNELS.map(ch => (
          <div key={ch} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div onClick={() => toggleChannel(ch)} style={toggleStyle(channelEnabled[ch], t)}>
              <div style={toggleKnob(channelEnabled[ch])} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: sans }}>
              {CHANNEL_LABELS[ch]}
            </span>
          </div>
        ))}
      </div>

      {/* Event Preferences */}
      <div style={sectionTitleStyle(t)}>Event Preferences</div>

      <div style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 32,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
          <thead>
            <tr style={{ background: t.bgSurface }}>
              <th style={{
                padding: '10px 14px', textAlign: 'left',
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 0.5, color: t.textMuted,
                borderBottom: '1px solid ' + t.border, width: '50%',
              }}>
                Event
              </th>
              {CHANNELS.map(ch => (
                <th key={ch} style={{
                  padding: '10px 14px', textAlign: 'center',
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: channelEnabled[ch] ? t.textMuted : t.textFaint,
                  borderBottom: '1px solid ' + t.border,
                }}>
                  {CHANNEL_LABELS[ch]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EVENT_GROUPS.map(g => (
              <React.Fragment key={g.group}>
                <tr>
                  <td colSpan={4} style={{
                    padding: '8px 14px',
                    fontSize: 11, fontWeight: 700, color: t.textSecondary,
                    fontFamily: sans, background: t.bgSurface,
                    borderBottom: '1px solid ' + t.borderLight,
                  }}>
                    {g.group}
                  </td>
                </tr>
                {g.events.map(evt => (
                  <tr key={evt.key} style={{ borderBottom: '1px solid ' + t.borderLight }}>
                    <td style={{ padding: '8px 14px 8px 28px', color: t.textPrimary }}>
                      {evt.label}
                    </td>
                    {CHANNELS.map(ch => (
                      <td key={ch} style={{ padding: '8px 14px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <div
                            onClick={() => channelEnabled[ch] && toggleEvent(evt.key, ch)}
                            style={{
                              ...toggleStyle(eventToggles[evt.key]?.[ch] && channelEnabled[ch], t),
                              opacity: channelEnabled[ch] ? 1 : 0.3,
                              cursor: channelEnabled[ch] ? 'pointer' : 'default',
                            }}
                          >
                            <div style={toggleKnob(eventToggles[evt.key]?.[ch] && channelEnabled[ch])} />
                          </div>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delivery Frequency */}
      <div style={sectionTitleStyle(t)}>Delivery Frequency</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 32 }}>
        {[
          { value: 'realtime', label: 'Real-time' },
          { value: 'daily', label: 'Daily digest' },
          { value: 'weekly', label: 'Weekly digest' },
        ].map(opt => (
          <label key={opt.value} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
          }}>
            <input
              type="radio"
              name="frequency"
              value={opt.value}
              checked={frequency === opt.value}
              onChange={() => { setFrequency(opt.value); markDirty(); }}
              style={{ accentColor: green }}
            />
            {opt.label}
          </label>
        ))}
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!dirty}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 6,
            background: dirty ? green : t.border,
            color: dirty ? '#fff' : t.textMuted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: sans,
            cursor: dirty ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          Save
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: green, fontFamily: sans, fontWeight: 500 }}>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
